import React, { useMemo, useState, useRef, useEffect } from 'react';
import { MessageCircle, ChevronDown, Search, Send, Bot } from 'lucide-react';
import { apiClient } from '../apiClient';

const FAQ_DATA = [
  {
    category: 'Getting Started',
    items: [
      {
        question: 'How do I create my first prompt?',
        answer:
          'Go to “Create Prompt” in the left menu, enter the prompt name and description, select a category, then build your prompt template. Use {{variable_name}} to create variables. Step 2 lets you configure each variable type and options.'
      },
      {
        question: 'What are variables and how do they work?',
        answer:
          'Variables are placeholders in your template like {{audience}}. They become inputs in the next step so users can fill them in. When you run or complete a prompt, Rebel Prompts injects the values into the template.'
      },
      {
        question: 'What is the difference between a Prompt, Project, and Folder?',
        answer:
          'Projects are top-level containers. Folders live inside a project and group related prompts. Prompts are the actual templates you run, share, and save outputs from.'
      }
    ]
  },
  {
    category: 'Projects & Folders',
    items: [
      {
        question: 'How do I organize prompts into folders?',
        answer:
          'Create a project, then open it to create folders. When creating a prompt, select the destination project and folder so it appears in the right place.'
      },
      {
        question: 'Can I make projects or folders public?',
        answer:
          'Yes. Set visibility to Public during creation or edit. Public items can be discovered and shared. Private items are only visible to you.'
      },
      {
        question: 'What happens when I delete a project or folder?',
        answer:
          'Deleted projects, folders, and prompts are moved to Trash for 30 days. You can restore them from the Trash page or permanently delete them.'
      }
    ]
  },
  {
    category: 'Prompt Runs & Saved Outputs',
    items: [
      {
        question: 'How do I save a completed prompt?',
        answer:
          'After you click “Complete Prompt,” a “Save Prompt” button appears. This saves the rendered prompt and variable inputs so you can reuse them later.'
      },
      {
        question: 'Where do saved prompts live?',
        answer:
          'Saved outputs are stored in the Saved Prompts page. You can select previous saves, reuse inputs, rename saves, or delete them.'
      },
      {
        question: 'Can I clear my inputs without deleting saved outputs?',
        answer:
          'Yes. Use “Clear Inputs” on a prompt page to reset fields without touching your saved outputs.'
      }
    ]
  },
  {
    category: 'Sharing & Access',
    items: [
      {
        question: 'How do invite links and access grants work?',
        answer:
          'You can create a share link or grant access by email/handle. Access can be revoked any time. Project access grants access to its folders and prompts; folder access grants its prompts; prompt access is prompt-only.'
      },
      {
        question: 'What does public vs private mean?',
        answer:
          'Public items can be viewed by anyone and appear in discovery pages. Private items are visible only to you (and any invitees you grant access to).'
      }
    ]
  },
  {
    category: 'Prompt Library & Favorites',
    items: [
      {
        question: 'How does the Prompt Library work?',
        answer:
          'The library lists public prompts, projects, and folders by category. Use search and sort to find what you need.'
      },
      {
        question: 'What are Favorite Prompts?',
        answer:
          'Click the star icon on a prompt to add it to your Favorites. You can remove it by clicking the star again.'
      }
    ]
  },
  {
    category: 'Social Hub',
    items: [
      {
        question: 'What is the Social Hub?',
        answer:
          'The Social Hub is a builder community with guilds (communities), posts, and threaded comments. It’s designed for sharing prompts, workflows, and results.'
      },
      {
        question: 'How do I create a post?',
        answer:
          'Open the Social Hub, click “Create Post,” pick a guild, then write your post. You can attach images and embed prompts/projects.'
      },
      {
        question: 'How do reporting and moderation work?',
        answer:
          'You can report content using the report option on posts. Moderators and admins review and act on reports.'
      }
    ]
  },
  {
    category: 'API & Integrations',
    items: [
      {
        question: 'How do I use the Prompt Hosting API?',
        answer:
          'Go to the API page to create a key, then call the v1 hosting endpoints shown there. You can fetch the latest template or saved outputs securely using your key.'
      },
      {
        question: 'Where are the API endpoints?',
        answer:
          'Visit the API page in the app menu. It shows the base URL, endpoints, and copy buttons.'
      }
    ]
  },
  {
    category: 'Troubleshooting',
    items: [
      {
        question: 'My login says “Failed to fetch.” What do I do?',
        answer:
          'Make sure the backend server is running and your app is pointed at the correct backend URL (default http://localhost:8001 in local dev).'
      },
      {
        question: 'The site won’t load locally.',
        answer:
          'Confirm both servers are running: backend on :8001 and frontend on :3000. Restart them if needed.'
      }
    ]
  }
];

function HelpPage() {
  const [query, setQuery] = useState('');
  const [openQuestion, setOpenQuestion] = useState(null);
  const [showBot, setShowBot] = useState(false);
  const [botInput, setBotInput] = useState('');
  const [botMessages, setBotMessages] = useState([
    { from: 'bot', text: 'Hi! Ask me anything about Rebel Prompts and I’ll find the best answer.' }
  ]);
  const [botBusy, setBotBusy] = useState(false);
  const botBodyRef = useRef(null);

  const filteredFaq = useMemo(() => {
    if (!query.trim()) return FAQ_DATA;
    const term = query.toLowerCase();
    return FAQ_DATA.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.question.toLowerCase().includes(term) ||
          item.answer.toLowerCase().includes(term)
      )
    })).filter((section) => section.items.length > 0);
  }, [query]);

  const handleAskBot = async () => {
    if (!botInput.trim()) return;
    const question = botInput.trim();
    setBotMessages((prev) => [...prev, { from: 'user', text: question }]);
    setBotInput('');
    setBotBusy(true);
    try {
      const data = await apiClient.post('/api/helpbot/ask', { question });
      setBotMessages((prev) => [...prev, { from: 'bot', text: data.answer }]);
    } catch (err) {
      setBotMessages((prev) => [
        ...prev,
        { from: 'bot', text: 'Support bot is unavailable right now. Please try again later.' }
      ]);
    } finally {
      setBotBusy(false);
    }
  };

  useEffect(() => {
    if (!botBodyRef.current) return;
    botBodyRef.current.scrollTop = botBodyRef.current.scrollHeight;
  }, [botMessages, showBot]);

  return (
    <div className="min-h-screen projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1 className="page-title">Help Center</h1>
            <p className="page-subtitle">Find answers quickly, or ask the Rebel Support Bot.</p>
          </div>
          <button className="btn-secondary" onClick={() => setShowBot(true)}>
            <MessageCircle className="w-4 h-4" /> Open Help Bot
          </button>
        </div>
        <div className="page-header-actions">
          <div className="search-field">
            <Search className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search help topics"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {filteredFaq.length === 0 && (
        <div className="card-glass text-gray-400">No matching help articles found.</div>
      )}

      <div className="space-y-6">
        {filteredFaq.map((section) => (
          <div key={section.category} className="card-glass">
            <h2 className="text-xl font-semibold mb-4">{section.category}</h2>
            <div className="space-y-3">
              {section.items.map((item) => {
                const isOpen = openQuestion === item.question;
                return (
                  <div key={item.question} className="faq-item">
                    <button
                      className="faq-question"
                      onClick={() => setOpenQuestion(isOpen ? null : item.question)}
                    >
                      <span>{item.question}</span>
                      <ChevronDown className={`w-4 h-4 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && <div className="faq-answer">{item.answer}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className={`help-bot ${showBot ? 'open' : ''}`}>
        <div className="help-bot-header">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            Rebel Support Bot
          </div>
          <button className="icon-btn" onClick={() => setShowBot(false)}>
            ×
          </button>
        </div>
        <div className="help-bot-body" ref={botBodyRef}>
          {botMessages.map((msg, index) => (
            <div key={index} className={`help-bot-msg ${msg.from}`}>
              {msg.text}
            </div>
          ))}
        </div>
        <div className="help-bot-input">
          <input
            className="input-glass"
            placeholder="Ask a question..."
            value={botInput}
            onChange={(e) => setBotInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAskBot();
            }}
            disabled={botBusy}
          />
          <button className="btn-primary" onClick={handleAskBot} disabled={botBusy}>
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default HelpPage;
